import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Heading, Form, Text, Link } from '@puppet/react-components';
import useWorkspaceDomain from '@hooks/useWorkspaceDomain';
import {
  useListEnvironmentsV2,
  useListPeIntegrationsV2,
} from '@services/cd4pe/peIntegrations';
import { useListBranchesV1, useListCommitsV1 } from '@services/cd4pe/vcs';
import {
  CodeProjectDetailsV1,
  PEIntegrationEnvironmentV2,
  PEIntegrationV2,
  ProjectPipelineStageV1,
  ProjectTypeV1,
} from '@puppet/cd4pe-client-ts';
import Cd4peError from '@components/Cd4peError';
import docsLinks from '@codeDelivery/utils/docsLinks';
import { checkDeploymentExists } from '@codeDelivery/components/ViewPipeline/utils';
import useListControlReposV1 from '@services/cd4pe/controlRepos';
import useDebounce from '@hooks/useDebounce';
import {
  formatControlReposForSelect,
  getBranchField,
  getCommitField,
  getControlRepoField,
  getEnvironmentField,
  getPuppetEnterpriseServerField,
} from '../../formHelpers';

export type ImpactAnalysisFormProps = {
  branch: string;
  commit: string;
  puppetEnterpriseServer: string;
  environment: string;
  nodes: number;
  allStageEnvironments: boolean;
  stageEnvironments: Record<string, boolean>;
  stageEnvironmentsControlRepos: Record<string, string>;
  controlRepo?: string;
};

const defaultProps = {
  submittable: true,
  cancellable: true,
};

export type ImpactAnalysis = {
  branch?: string;
  commit?: string;
  puppetEnterpriseServer?: PEIntegrationV2 | undefined;
  environment?: PEIntegrationEnvironmentV2 | undefined;
  nodes: number;
  allStageEnvironments?: boolean;
  stageEnvironments?: string[];
  stageEnvironmentsControlRepos?: Record<string, string>;
  controlRepo?: string;
};

interface Props {
  type: 'manual' | 'stage';
  projectType: ProjectTypeV1;
  stages?: ProjectPipelineStageV1[];
  initialValues?: ImpactAnalysisFormProps;
  codeProject: CodeProjectDetailsV1;
  disabled?: boolean;
  submittable?: boolean;
  cancellable?: boolean;
  onSubmit: (values: ImpactAnalysis) => void;
  onCancel: () => void;
  submitting: boolean;
  submitKey?: string;
  cancelKey?: string;
}

const ImpactAnalysisForm = ({
  type,
  projectType,
  stages = [],
  initialValues,
  codeProject,
  disabled,
  submittable,
  cancellable,
  onSubmit,
  onCancel,
  submitting,
  submitKey,
  cancelKey,
}: Props) => {
  const { t } = useTranslation('codeDelivery');
  const workspaceId = useWorkspaceDomain();
  const [NoEnvSelected, setNoEnvSelected] = useState(false);

  const [formValues, setFormValues] = useState<ImpactAnalysisFormProps>(
    initialValues || {
      branch: '',
      commit: '',
      puppetEnterpriseServer: '',
      environment: '',
      nodes: 10,
      allStageEnvironments: projectType === 'CONTROL_REPO',
      stageEnvironments: {},
      stageEnvironmentsControlRepos: {},
      controlRepo: '',
    },
  );

  const vcsBranches = useListBranchesV1(
    {
      workspaceId,
      provider: codeProject.srcRepoProvider,
      name: codeProject.srcRepoDisplayName,
      organization: codeProject.srcRepoOwner,
      project: codeProject.srcRepoId,
      prefix: useDebounce(formValues.branch),
    },
    {
      enabled: type === 'manual',
      keepPreviousData: true,
    },
  );

  const vcsCommits = useListCommitsV1(
    {
      workspaceId,
      provider: codeProject.srcRepoProvider,
      name: codeProject.srcRepoDisplayName,
      organization: codeProject.srcRepoOwner,
      project: codeProject.srcRepoId,
      branch: formValues.branch,
    },
    {
      enabled: type === 'manual' && formValues.branch !== '',
    },
  );

  const peIntegrations = useListPeIntegrationsV2(
    {
      workspaceId,
      peIntegrationNamePrefix: useDebounce(formValues.puppetEnterpriseServer),
    },
    {
      enabled: type === 'manual',
      keepPreviousData: true,
    },
  );

  const peIntegrationMatch = peIntegrations.data?.peIntegrations?.find(
    (peIntegration) => peIntegration.name === formValues.puppetEnterpriseServer,
  );

  const environments = useListEnvironmentsV2(
    {
      peIntegrationId: peIntegrationMatch?.id ?? '',
      environmentPrefix: useDebounce(formValues.environment),
    },
    {
      enabled: type === 'manual' && !!peIntegrationMatch,
      keepPreviousData: true,
    },
  );

  const controlRepos = useListControlReposV1(
    {
      workspaceId,
      prefix: useDebounce(formValues.controlRepo ?? ''),
    },
    {
      enabled: projectType === 'MODULE',
    },
  );
  const controlRepoOptions = formatControlReposForSelect(controlRepos);

  const onChangeHandler = (
    nameChanged: keyof ImpactAnalysisFormProps,
    values: ImpactAnalysisFormProps,
  ) => {
    let newValuesCopy: ImpactAnalysisFormProps;

    if (NoEnvSelected) {
      setNoEnvSelected(false);
    }

    switch (nameChanged) {
      case 'branch':
        newValuesCopy = {
          ...values,
          commit: '',
          puppetEnterpriseServer: '',
          environment: '',
        };
        break;
      case 'commit':
        newValuesCopy = {
          ...values,
          puppetEnterpriseServer: '',
          environment: '',
        };
        break;
      case 'puppetEnterpriseServer':
        newValuesCopy = {
          ...values,
          environment: '',
        };
        break;
      default:
        newValuesCopy = values;
        break;
    }

    setFormValues(newValuesCopy);
  };

  const onSubmitHandler = (submitValues: ImpactAnalysisFormProps) => {
    const selectedEnvironments = Object.keys(
      formValues.stageEnvironments,
    ).filter((key) => formValues.stageEnvironments[key]);

    if (
      (type === 'stage' &&
        !formValues.allStageEnvironments &&
        selectedEnvironments.length === 0) ||
      (type === 'stage' && !checkDeploymentExists(stages))
    ) {
      setNoEnvSelected(true);
      return;
    }

    let submitPayload: ImpactAnalysis = {
      nodes: submitValues.nodes,
    };

    const puppetEnterpriseServer = peIntegrations.data?.peIntegrations?.find(
      (peIntegration) =>
        peIntegration.name === submitValues.puppetEnterpriseServer,
    );

    const environment = environments?.data?.environments?.find(
      (group) => group.name === submitValues.environment,
    );

    if (type === 'stage') {
      const stageEnvironments: ImpactAnalysis['stageEnvironments'] = [];
      const stageEnvironmentsControlRepos: ImpactAnalysis['stageEnvironmentsControlRepos'] =
        {};

      Object.entries(submitValues.stageEnvironments).forEach(
        ([environmentName, environmentEnabled]) => {
          if (environmentEnabled) {
            stageEnvironments.push(environmentName);

            const controlRepoName =
              submitValues.stageEnvironmentsControlRepos[environmentName];
            if (controlRepoName) {
              stageEnvironmentsControlRepos[environmentName] = controlRepoName;
            }
          }
        },
      );

      submitPayload = {
        ...submitPayload,
        allStageEnvironments: submitValues.allStageEnvironments,
        stageEnvironments,
      };

      if (projectType === 'MODULE') {
        submitPayload = {
          ...submitPayload,
          stageEnvironmentsControlRepos,
          controlRepo: submitValues.controlRepo,
        };
      }

      onSubmit(submitPayload);
    } else {
      submitPayload = {
        ...submitPayload,
        branch: submitValues.branch,
        commit: submitValues.commit,
        puppetEnterpriseServer,
        environment,
      };

      if (projectType === 'MODULE') {
        submitPayload = {
          ...submitPayload,
          controlRepo: submitValues.controlRepo,
        };
      }

      onSubmit(submitPayload);
    }
  };

  const getEnvironmentFields = () => {
    if (stages.length === 0) {
      return null;
    }

    return stages
      .flatMap((value) => value.destinations)
      .filter((destination) => !!destination.deploymentTemplate)
      .map((destination) => (
        <div className="impact-analysis-form__environment-field">
          <Form.Field
            key={destination.id}
            type="checkbox"
            name={`stageEnvironments.${destination.id}`}
            path={`stageEnvironments.${destination.id}`}
            data-testid={`stageEnvironments.${destination.id}`}
            label={
              <div className="environment-checkbox-label">
                <div>
                  <Trans
                    parent="div"
                    t={t}
                    i18nKey="viewPipeline.form.impactAnalysis.field.stageEnvironment.policy"
                    values={{
                      policy:
                        destination.deploymentTemplate?.deploymentPolicy
                          ?.displayName,
                    }}
                  >
                    <span className="environment-checkbox-label__value" />
                  </Trans>
                  <Trans
                    parent="div"
                    t={t}
                    i18nKey="viewPipeline.form.impactAnalysis.field.stageEnvironment.instance"
                    values={{
                      instance:
                        destination.deploymentTemplate
                          ?.puppetEnterpriseCredentialsId?.name,
                    }}
                  >
                    <span className="environment-checkbox-label__value" />
                  </Trans>
                </div>
                <div>
                  <Trans
                    parent="div"
                    t={t}
                    i18nKey="viewPipeline.form.impactAnalysis.field.stageEnvironment.environment"
                    values={{
                      environment:
                        destination.deploymentTemplate?.deploymentTarget
                          ?.nodeGroupName,
                    }}
                  >
                    <span className="environment-checkbox-label__value" />
                  </Trans>
                  <Trans
                    parent="div"
                    t={t}
                    i18nKey="viewPipeline.form.impactAnalysis.field.stageEnvironment.stageNum"
                    values={{ stageNum: destination.stageNum }}
                  >
                    <span className="environment-checkbox-label__value" />
                  </Trans>
                </div>
              </div>
            }
            value={
              destination.deploymentTemplate?.deploymentTarget?.nodeGroupName
            }
          />
          {projectType === 'MODULE' && (
            <div
              data-testid={`stageEnvironmentsControlRepos.${destination.id}`}
            >
              <Form.Field
                key={`${destination.id}-controlRepo`}
                className="impact-analysis-form__environment-field-control-repo"
                name={`stageEnvironmentsControlRepos.${destination.id}`}
                path={`stageEnvironmentsControlRepos.${destination.id}`}
                type="select"
                options={controlRepoOptions}
                placeholder={t(
                  'viewPipeline.form.field.environment.controlRepo.placeholder',
                )}
                required={formValues.stageEnvironments[destination.id!]}
                selectProps={{ getPopupContainer: () => document.body }}
              />
            </div>
          )}
        </div>
      ));
  };

  const branchMatch = vcsBranches?.data?.vcsBranches?.some(
    (branch) => branch.name === formValues.branch,
  );

  const commitMatch = vcsCommits?.data?.vcsCommits?.some(
    (commit) => commit.sha === formValues.commit,
  );

  const environmentMatch = environments.data?.environments?.find(
    (environment) => environment.name === formValues.environment,
  );

  return (
    <>
      <Form
        className="impact-analysis-form"
        initialValues={formValues}
        disabled={disabled}
        onChange={onChangeHandler}
        cancellable={cancellable}
        submittable={submittable}
        onSubmit={onSubmitHandler}
        onCancel={onCancel}
        submitting={submitting}
        submitLabel={submitKey ? t(submitKey) : undefined}
        cancelLabel={cancelKey ? t(cancelKey) : undefined}
        error={
          NoEnvSelected
            ? t('viewPipeline.form.impactAnalysis.error.noDeploymentSelected')
            : null
        }
        data-testid="ia-form"
      >
        {type === 'manual' &&
          getBranchField({
            query: vcsBranches,
            fieldValue: formValues.branch,
            t,
          })}
        {type === 'manual' &&
          branchMatch &&
          getCommitField({
            query: vcsCommits,
            fieldValue: formValues.commit,
            branch: formValues.branch,
            codeProject,
            t,
          })}
        {type === 'manual' &&
          commitMatch &&
          getPuppetEnterpriseServerField({
            query: peIntegrations,
            fieldValue: formValues.puppetEnterpriseServer,
            t,
          })}
        {type === 'manual' &&
          peIntegrationMatch &&
          getEnvironmentField({
            query: environments,
            fieldValue: formValues.environment,
            codeProject,
            t,
          })}
        {type === 'stage' && (
          <div className="impact-analysis-form__stage-impact-analysis">
            <Link as="a" href={docsLinks().impactAnalysis} target="_blank">
              {t('viewPipeline.form.impactAnalysis.stage.whatIsIA.heading')}
            </Link>
            <Text size="small">
              {t('viewPipeline.form.impactAnalysis.stage.whatIsIA.description')}
            </Text>
          </div>
        )}
        {(type === 'stage' || formValues.environment !== '') && (
          <Text className="impact-analysis-form__nodes" size="small">
            <Trans t={t} i18nKey="viewPipeline.form.impactAnalysis.field.nodes">
              <Form.Field
                className="impact-analysis-form__nodes-field"
                name="nodes"
                label=""
                type="number"
                data-testid="nodes"
                required
              />
            </Trans>
          </Text>
        )}
        {type === 'manual' &&
          projectType === 'MODULE' &&
          environmentMatch &&
          getControlRepoField({
            query: controlRepos,
            fieldValue: formValues.controlRepo ?? '',
            t,
          })}
        {type === 'stage' && (
          <>
            <Heading
              className="impact-analysis-form__analyze-environments-heading"
              as="h6"
            >
              {t(
                'viewPipeline.form.impactAnalysis.field.stageEnvironment.label',
              )}
            </Heading>
            {projectType === 'CONTROL_REPO' && (
              <Form.Field
                type="switch"
                name="allStageEnvironments"
                label={t(
                  'viewPipeline.form.impactAnalysis.field.allStageEnvironments.label',
                )}
                data-testid="allStageEnvironments"
              />
            )}
          </>
        )}
        {type === 'stage' && (
          <div className="impact-analysis-form__environment-fields-wrapper">
            {!formValues.allStageEnvironments && getEnvironmentFields()}
          </div>
        )}
      </Form>
      <Cd4peError error={vcsBranches.error} />
      <Cd4peError error={vcsCommits.error} />
      <Cd4peError error={peIntegrations.error} />
      <Cd4peError error={environments.error} />
    </>
  );
};

export default ImpactAnalysisForm;

ImpactAnalysisForm.defaultProps = defaultProps;
